<template>
  <!--<div class="dt-fixedcolumns-container" style="width: 100%; overflow: hidden;">-->
  <div class="container-flex w-100 position-relative">
    <DataTable 
      class="table table-hover table-striped w-100" 
      :data="computedTableData" 
      :columns="computedColumns" 
      :options="tableOptions"
      ref="tableRef" 
    >
      <!-- Natively forward all parental scoped slots down to DataTables -->
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import DataTable from 'datatables.net-vue3';
import { isPlainObject, camelCase } from 'lodash-es';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { sortArrayByKey, toBool } from '@/utils/helpers';
import { applyTooltip } from '@/directives/tooltip';
import { apiFetch } from '@/utils/api';
import { logVerbose } from '@/utils/config';

const auth = useAuthStore();
const schema = useSchemaStore();

const props = defineProps({
  modelName: { type: String, default: 'Item' },
  columns: { type: Array, required: true },
  data: { type: Array, default: () => [] }, // Used for client-side exceptions like Beams
  mockSource: { type: String, default: '' },
  pageLength: { type: Number, default: 10 },
  options: { type: Object, default: () => ({}) }, // Custom overrides
  orderBy: { type: Object, default: null }, // { colName: dir }
  ajaxUrl: { type: String, default: '' }, // implies Server-Side Processing
  queryParams: { type: Object, default: {} }, // for Server-Side Processing
});

const emit = defineEmits(['row-click', 'row-update']);

const tableRef = ref(null);
const visibleRows = ref([]);
const localMockData = ref([]); // Local cache for client-side mock loading
const isServerSide = computed(() => props.ajaxUrl.length > 0);

// Choose whether DataTables renders from prop data or client-side mock data cache
const computedTableData = computed(() => {
  if (isServerSide.value) return undefined;
  return props.mockSource ? localMockData.value : props.data;
});

const cleanValue = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return val;
};

// Process inputs dynamically across both SSP and Client-side rows
const computedColumns = computed(() => {
  if (!Array.isArray(props.columns)) return [];

  return props.columns.map((col) => {
    // If the column doesn't have an editType configuration specified, leave it completely stock
    if (!col.editType) return col;

    return {
      ...col,
      // Inject DataTables native layout render function override hook
      render: (data, type, row, meta) => {
        // DataTables invokes render for display, sorting, filtering, etc. 
        if (type !== 'display') {
          return row[col.data];
        }
        let value = row[col.data];
        const dataIdKey = col.dataId;
        // Dynamic Primary Key Tracker: Resolves database IDs or defaults securely to cell row indices
        const dataId = dataIdKey && row[dataIdKey] !== undefined 
          ? row[dataIdKey] 
          : `row${meta.row}`;
        // Dynamic Form Control Factory Router
        switch (col.editType) {
          case 'checkbox': {
            // Ensure you add standard class styling parameters matching your bootstrap theme layers
            const isChecked = ['true', '1', 'checked'].includes(`${value}`);
            return `<div class="form-check d-flex justify-content-center m-0">
                      <input 
                        type="checkbox" class="form-check-input" ${isChecked ? 'checked' : ''} 
                        data-id="${dataId}" data-col="${col.data}" style="cursor: pointer;"
                      />
                    </div>`;
          }
          
          case 'text': {
            return `<input 
                      type="text" class="form-control form-control-sm border-secondary" 
                      value="${value ?? ''}" data-id="${dataId}" data-col="${col.data}"
                    />`;
          }
          
          case 'select': {
            // Support unwrapping if options is passed as a Vue computed reference array string
            const optionsArray = computed(() => {
              if (!col.options) return [];
              return typeof col.options.value !== 'undefined' ? col.options.value : col.options;
            }).value;

            const selectOptions = optionsArray.map(opt => {
                const optVal = isPlainObject(opt) ? opt.value : opt;
                const optLabel = isPlainObject(opt) ? opt.label : opt;
                return `<option value="${optVal}" ${value === optVal 
                  ? 'selected' 
                  : ''}>${optLabel}</option>`;
              })
              .join('');

            return `<select data-id="${dataId}" data-col="${col.data}" 
                      class="form-select form-select-sm border-secondary" 
                      style="min-width: 120px;">
                      ${selectOptions}
                    </select>`;
          }
          
          default: {
            return value ?? '';
          }
        }
      },
    };
  });
});

const addHeaderTooltip = (th, col) => {
  if (th && !th.querySelector('[data-bs-toggle="tooltip"]')) {
    th.innerHTML = `
      <div class="d-flex align-items-center justify-content-center" 
          style="cursor: help; white-space: nowrap;">
        <span data-bs-toggle="tooltip" title="${col.tooltip}">
          ${col.title}
          <i class="bi bi-info-circle ms-1 text-primary" 
              style="font-size: 0.85rem;">
          </i>
        </span>
      </div>
    `;
  }
};

// Merge baseline settings with any unique overrides passed via :options
const tableOptions = computed(() => {
  // Determine sort order
  let order;
  if (isPlainObject(props.orderBy)) {
    if (Object.keys(props.orderBy).length !== 1) {
      throw new Error(`Invalid orderBy configuration`);
    }
    const dataId = Object.keys(props.orderBy)[0];
    const dir = `${Object.values(props.orderBy)[0]}`.toLowerCase();
    if (!['asc', 'desc'].includes(dir)) {
      throw new Error(`Invalid orderby direction`);
    }
    const colIdx = props.columns.findIndex(c => c.data === dataId) || 0;
    order = [[ colIdx, dir ]];
  }
  // Configure defaults
  const defaults = {
    autoWidth: true,
    order: order || [[0, 'asc']],
    pageLength: props.pageLength,
    layout: {
      topStart: ['info'],
      topEnd: ['pageLength', 'search'],
      bottomStart: null,
      bottomEnd: 'paging',
    },
    responsive: false,
    colReorder: true,
    fixedColumns: { left: 1, right: auth.isAdmin ? 2 : 1 },
    scrollX: true,
    // scrollCollapse: true,
    stateSave: true,
    language: {
      loadingRecords: `Loading ${props.modelName}s...`,
      emptyTable: `No ${props.modelName} data found matching filters`,
      info: `<b>_TOTAL_</b> ${props.modelName}s total (showing _START_ to _END_)`,
      infoFiltered: `(filtered from _MAX_)`,
      lengthMenu: `_MENU_`,
    },

    createdRow: function(row, data, dataIndex) {
      // Check if the current data record has a truthy isLocked parameter
      if (data && toBool(data.isLocked)) {
        // Append a custom target class straight onto the <tr> element node
        row.classList.add('table-row-locked');
      }
    },
    
    initComplete: function() {
      const api = this.api();
      // Apply formatting for headers with custom tooltips (once)
      props.columns.forEach((col, idx) => {
        if (col.tooltip) {
          const th = api.column(idx).header();
          addHeaderTooltip(th, col);
        }
      });
      // Attempt to remove horizontal scroll bar when no content underneath
      // setTimeout(() => {
      //   api.columns.adjust().fixedColumns().relayout();
      // }, 150);
    },

    drawCallback: function(settings) {
      const api = this.api();
      const tag = '[data-bs-toggle="tooltip"]';
      
      props.columns.forEach((col, idx) => {
        if (col.tooltip) {
          const th = api.column(idx).header();
          addHeaderTooltip(th, col);
        }
      });
      
      const headerContainer = api.table().header();
      const headerTriggers = headerContainer 
        ? headerContainer.querySelectorAll(tag) 
        : [];
      const tableContainer = settings.nTableWrapper;
      const rowTriggers = tableContainer 
        ? tableContainer.querySelectorAll(tag) 
        : [];
      const triggers = [...headerTriggers, ...rowTriggers];
      triggers.forEach(el => {
        applyTooltip(el);
      });
      // Cleanup any orphaned DOM nodes
      document.querySelectorAll('.tooltip').forEach(node => node.remove());
    },
  };

  const options = { ...defaults, ...props.options };

  // Turn on DataTables native Server-Side Handling dynamically
  if (isServerSide.value) {
    const conflicts = ['order', 'stateSave'];
    for (const c of conflicts) {
      delete options[c];
    }
    Object.assign(options, {
      serverSide: true,
      processing: true,
      search: { return: true },
      ajax: async (data, callback, settings) => {
        // 'data' contains DataTables' current state:
        // start (offset), length (page size), search.value, draw, etc.
        try {
          const draw = data.draw;
          let response;
          if (props.mockSource) {
            response = await mockResponse();
          } else {
            const colIdx = data.order[0]?.column;
            const colName = props.columns[colIdx]?.data;
            const tableOrder = { [colName]: data.order[0]?.dir };
            response = await apiFetch(props.ajaxUrl, {
              method: 'GET',
              // Pass DataTables params as query string
              params: {
                offset: data.start,
                limit: data.length,
                search: data.search.value || undefined,
                // draw: data.draw,
                orderBy: props.orderBy || tableOrder,
                ...props.queryParams,
              }
            });
          }
          if (logVerbose && response.items) {
            console.groupCollapsed(
              `Received ${response.items.length} ${props.modelName}s like...`
            );
            console.table(response.items.slice(0, 2));
            console.groupEnd();
          }
          const recordsTotal = auth.isAdmin
            ? response.total
            : response.total - (response.locked || 0);
          const recordsFiltered = auth.isAdmin
            ? response.filtered
            : response.filtered - (response.locked || 0);
          callback({
            draw: draw, // Echo the draw counter
            recordsTotal: recordsTotal, // Full DB count
            recordsFiltered: recordsFiltered, // Count after search filter
            data: response.items // The actual array of rows
          });
        } catch (err) {
          console.error("Table fetch failed:", err);
          // Inform DataTables to stop the loading spinner
          callback({ data: [] }); 
        }
      },
    });
  }

  return options;
});

const getPrimaryKey = async () => {
  try {
    return await schema.getModelPrimaryKey(camelCase(props.modelName));
  } catch {
    return 'id';
  }
};

const mockResponse = async () => {
  const response = { items: [], total: 0, filtered: 0, locked: 0 };
  if (!props.mockSource) return response;
  ({ default: response.items } = await import(/* @vite-ignore */props.mockSource)
    .catch(err => console.error(`No mock ${props.modelName} data found`))
  );
  response.total = response.items.length;
  if (response.total === 0) return response;
  for (const k in props.queryParams) {
    if (k === 'orderBy') {
      const orderKey = Object.keys(props.queryParams[k])[0];
      const orderDir = Object.values(props.queryParams[k])[0];
      response.items = sortArrayByKey(response.items, orderKey, orderDir);
    }
    if (k === 'limit') {
      const offset = props.queryParams.offset || 0;
      response.items = response.items.slice(offset, props.queryParams[k]);
    }
  }
  response.filtered = response.items.length;
  response.locked = response.items.reduce(
    (acc, item) => item.isLocked === true ? acc + 1 : acc, 0
  );
  return response;
};

const reloadTable = async (resetPaging = false) => {
  if (isServerSide.value && tableRef.value?.dt) {
    // DataTables native asynchronous source invalidation call pipeline
    tableRef.value.dt.ajax.reload(null, resetPaging);
  } else if (props.mockSource) {
    ({ items: localMockData.value } = await mockResponse());
  }
};

defineExpose({ reloadTable, visibleRows });

// Watch and redraw local data instances (only for client-side tables)
watch(() => props.data, (newData) => {
  if (!isServerSide.value && tableRef.value?.dt) {
    tableRef.value.dt.clear().rows.add(newData).draw();
  }
}, { deep: true });

// Attach event delegation for clicks/changes inside cells
onMounted(() => {
  const dt = tableRef.value?.dt; // Get the DataTable instance
  
  if (dt) {
    // Sync data when DataTables redraws (handles Client-Side paging/sorting)
    dt.on('draw', () => {
      visibleRows.value = dt.rows({ page: 'current' }).data().toArray();
    });

    // Sync data when Server-Side API returns new data (handles SSP)
    dt.on('xhr', (e, settings, json) => {
      if (json && json.data) {
        visibleRows.value = json.data;
      }
    });
  }

  const tableEl = tableRef.value?.$el;
  if (!tableEl) return;

  const tbody = tableEl.querySelector('tbody');

  // Row Click Listener
  tbody?.addEventListener('click', (e) => {
    const tr = e.target.closest('tr');
    if (tr && tableRef.value?.dt) {
      const rowData = tableRef.value.dt.row(tr).data();
      emit('row-click', rowData); // Emits full row data to trigger your action modas/forms
    }
  }, { passive: true });

  // Input Field Change Listener (like your Beams checkboxes/text elements)
  tableEl.addEventListener('change', async (e) => {
    const target = e.target;
    if (['INPUT', 'SELECT'].includes(target.tagName) && tableRef.value?.dt) {
      const tr = target.closest('tr');
      const rowData = tableRef.value.dt.row(tr).data();
      const newValue = target.type === 'checkbox' ? target.checked : target.value;
      const primaryKey = await getPrimaryKey();
      if (isServerSide.value) {
        // For SSP, emit the single mutated object so the view can PATCH immediately
        emit('row-update', {
          [primaryKey]: rowData[primaryKey],
          field: target.dataset.col,
          value: newValue,
        });
      } else {  
        // For Client-Side, update the whole local list array
        const updatedData = props.data.map(row => {
          if (row[primaryKey] === rowData[primaryKey]) {
            return { ...row, [target.dataset.col]: newValue };
          }
          return row;
        });
        emit('row-update', updatedData);
      }
    }
  }, { passive: true });
});

</script>
<style scoped>

/* Format custom expandable JSON details layout */
:deep(details.json-cell-drawer) {
  display: block;
  background-color: transparent;
}

:deep(details.json-cell-drawer summary) {
  list-style: none;
  color: #0dcaf0 !important; /* Standard Bootstrap info cyan color marker */
  cursor: pointer;
  user-select: none;
}

:deep(details.json-cell-drawer summary::-webkit-details-marker) {
  display: none; /* Hide standard browser bullet points */
}

:deep(details.json-cell-drawer pre) {
  background-color: #111418 !important; /* Lean, dark background layer */
  border: 1px solid #495057 !important;
  color: #66d9ef !important;
  text-align: left;
  font-size: 0.75rem;
  max-height: 250px;
  overflow-y: auto;
  white-space: pre-wrap;
}

/* Target the row background and text color fields */
:deep(tr.table-row-locked) {
  color: var(--bs-danger, #dc3545) !important; /* Forces Bootstrap danger red */
  text-decoration: line-through !important;   /* Applies the strikethrough line */
}

/* Optional: Soften background highlight tracking for locked records */
:deep(tr.table-row-locked td) {
  color: inherit !important; /* Forces cells to respect the row level text rules */
}

/* Optional: Ensures form controls inside a locked row look deactivated */
:deep(tr.table-row-locked input),
:deep(tr.table-row-locked select) {
  color: var(--bs-danger, #dc3545) !important;
  text-decoration: line-through !important;
  opacity: 0.7;
}

</style>
